import { useMemo, useState } from "react";
import { toast } from "react-toastify";

export function SubscribeButton({
  vehicleList = [],
  onSubscriptionUpdate,
  organizationId,
  selectedVehicleIds = new Set(), // Set of unsubscribed selected vehicle IDs
}) {
  const organizationCode = sessionStorage.getItem("org_code");
  const [loading, setLoading] = useState(false);

  const totalVehicles = vehicleList.length;
  const selectedCount = selectedVehicleIds.size;

  const { trialCount, activeCount, unsubscribedCount } = useMemo(() => {
    return vehicleList.reduce(
      (counts, vehicle) => {
        if (vehicle.subscriptionStatus === "TRIAL") {
          counts.trialCount += 1;
        } else if (vehicle.subscriptionStatus === "ACTIVE") {
          counts.activeCount += 1;
        } else {
          counts.unsubscribedCount += 1;
        }

        return counts;
      },
      {
        trialCount: 0,
        activeCount: 0,
        unsubscribedCount: 0,
      },
    );
  }, [vehicleList]);

  const hasTrial = trialCount > 0;
  const allSubscribed = totalVehicles > 0 && unsubscribedCount === 0;

  const handleSubscribe = async () => {
    try {
      setLoading(true);

      if (!organizationCode || !organizationId) {
        throw new Error("Invalid session");
      }

      if (allSubscribed) {
        toast.info("Subscription not needed");
        return;
      }

      const apiUrl = import.meta.env.VITE_STRIPE_ADMIN_URL;

      const response = await fetch(
        `${apiUrl}/api/billing/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            organizationId,
            organizationCode,
            truckIds: Array.from(selectedVehicleIds), // ✅ convert Set to Array
          }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          toast.info(data.message || "All trucks already subscribed");
          return;
        }
        throw new Error(data.message || "Failed to checkout");
      }

      if (!data.checkoutUrl) {
        toast.success(data.message || "Trucks added successfully!");
        if (onSubscriptionUpdate) onSubscriptionUpdate();
        return;
      }

      window.location.assign(data.checkoutUrl);
    } catch (err) {
      console.error("Fetch error: ", err);
      toast.error(err.message || "Could not start subscription");
    } finally {
      setLoading(false);
    }
  };

  let buttonText = "Subscribe ($30/ truck)";
  let buttonColor = "#16a34a";
  let disabled = loading || selectedCount === 0;

  if (loading) {
    buttonText = "Processing...";
    buttonColor = "#16a34a";
  } else if (allSubscribed) {
    if (hasTrial) {
      buttonText = `✓ ${trialCount} truck${trialCount > 1 ? "s" : ""} in trial, ${activeCount} active`;
    } else {
      buttonText = "✓ All trucks active";
    }
    buttonColor = "#6b7280";
    disabled = true;
  } else {
    if (selectedCount > 0) {
      buttonText = `Subscribe ${selectedCount} truck${selectedCount > 1 ? "s" : ""} ($30/ truck)`;
      buttonColor = "#16a34a";
    } else {
      buttonText = `Select ${unsubscribedCount} truck${unsubscribedCount > 1 ? "s" : ""} ($30/ truck)`;
      buttonColor = "#6b7280";
      disabled = true;
    }
  }

  return (
    <button
      type="button"
      onClick={handleSubscribe}
      disabled={disabled}
      style={{
        ...styles.button,
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        backgroundColor: buttonColor,
      }}
    >
      {buttonText}
    </button>
  );
}

const styles = {
  button: {
    padding: "10px 16px",
    margin: "12px 16px",
    cursor: "pointer",
    color: "#ffffff",
    fontSize: "14px",
    letterSpacing: "1px",
    border: "none",
    borderRadius: "16px",
    transition: "all 0.2s ease",
    boxShadow: "0 2px 4px rgba(0,0,0,1)",
  },
};
